import { createContext, ReactNode, useMemo } from 'react';
import { ErrorFallbackScreen } from 'components/screens';
import { useAccessToken } from 'utils/auth';
import { logError } from 'utils/datadog';
import { useWeekPeriod } from '../hooks';
import { useWeekDetailsQuery } from '../query';
import { WeekDetailsContextState } from '../types';

export const WeekDetailsContext = createContext<
  WeekDetailsContextState | undefined
>(undefined);

export const WeekDetailsContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { token } = useAccessToken();

  const {
    weekPeriod: week,
    isLoading: isWeekLoading,
    weekId
  } = useWeekPeriod();

  const {
    data,
    isError,
    error,
    isLoading: isWeekDetailsLoading
  } = useWeekDetailsQuery(token, week?.calendarWeek, week?.year);

  const contextValue = useMemo(
    () => ({
      weekDetails: data?.data,
      isError: !!(token && weekId && !data?.data),
      isLoading: isWeekDetailsLoading || isWeekLoading
    }),
    [data?.data, token, weekId, isWeekDetailsLoading, isWeekLoading]
  );

  if (isError) {
    logError('Week details query error', error);
    return <ErrorFallbackScreen />;
  }

  return (
    <WeekDetailsContext.Provider value={contextValue}>
      {children}
    </WeekDetailsContext.Provider>
  );
};
